import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';
import { fetchRecipeIndex } from '../utils/recipeFetcher';
import { validateRecipeIndex } from '../utils/indexValidator';
import { truncateDescription } from '../utils/recipeParser';
import '../styles/recipes.css';

/**
 * RecipeCategoryPage displays recipes for a single category.
 * Fetches the recipe index at runtime and filters to the matching category.
 * Route: /recipes/:category
 */
const RecipeCategoryPage = () => {
  const { category } = useParams();
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecipeIndex()
      .then(data => {
        const validated = validateRecipeIndex(data);
        const found = validated.categories.find(c => c.name === category);
        setCategoryData(found || null);
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [category]);

  if (loading) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-loading-state" role="status" aria-label="Loading recipes">
            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            <p>Loading recipes...</p>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (error) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-error-state">
            <h2>Unable to Load Recipes</h2>
            <p>{error.message || 'An unexpected error occurred while loading recipes.'}</p>
            <Link to="/recipes">Back to Recipes</Link>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (!categoryData) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-error-state">
            <h2>Category Not Found</h2>
            <p>No recipe category named "{category}" was found.</p>
            <Link to="/recipes">Back to Recipes</Link>
          </div>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title={categoryData.displayName} icon="fas fa-utensils">
      <div className="section recipe-detail-section">
        {categoryData.description && (
          <p className="category-description">{categoryData.description}</p>
        )}
        <div className="card-grid">
          {categoryData.recipes.map((recipe) => (
            <Link
              key={recipe.slug}
              to={`/recipes/${recipe.category}/${recipe.slug}`}
              className="card-link"
            >
              <div className="card">
                <h3 className="card-title">{recipe.title}</h3>
                <p className="card-description">
                  {truncateDescription(recipe.description, 150)}
                </p>
                <div className="card-meta">
                  <span className={`badge badge-${recipe.difficulty}`}>
                    {recipe.difficulty}
                  </span>
                  <span className="card-time">
                    <i className="fas fa-clock" aria-hidden="true"></i>
                    {recipe.prepTime} + {recipe.cookTime}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PageTemplate>
  );
};

export default RecipeCategoryPage;
